import { Rocket, AlertCircle, Lightbulb } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DeepAnalysis } from '@/hooks/useDeepAnalysis';

interface CatalystsCardProps {
  analysis: DeepAnalysis;
}

export function CatalystsCard({ analysis }: CatalystsCardProps) {
  const hasAth = analysis.marketData?.ath !== null && analysis.marketData?.ath !== undefined && analysis.marketData.ath > 0;
  const hideAthCopy = (text: string) => hasAth || !/\bATH\b|all[- ]time high|from ATH|ath_price/i.test(text);
  const upcomingCatalysts = (Array.isArray(analysis.catalysts?.upcoming) ? analysis.catalysts.upcoming : []).filter(hideAthCopy);
  const riskCatalysts = (Array.isArray(analysis.catalysts?.risks) ? analysis.catalysts.risks : []).filter(hideAthCopy);
  const keyInsights = (Array.isArray(analysis.keyInsights) ? analysis.keyInsights : []).filter(hideAthCopy);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Rocket className="w-5 h-5 text-primary" />
          <CardTitle className="text-base">Catalysts & Key Insights</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Upcoming Catalysts */}
          <div className="p-3 bg-success/5 rounded-lg border border-success/20">
            <div className="flex items-center gap-2 mb-2">
              <Rocket className="w-4 h-4 text-success" />
              <span className="text-sm font-medium text-success">Upcoming Catalysts</span>
            </div>
            <ul className="space-y-2">
              {upcomingCatalysts.length > 0 ? upcomingCatalysts.map((catalyst, i) => (
                <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                  <span className="text-success">→</span>
                  {catalyst}
                </li>
              )) : <li className="text-sm text-muted-foreground">No specific upside catalysts identified from available data.</li>}
            </ul>
          </div>

          {/* Risk Catalysts */}
          <div className="p-3 bg-destructive/5 rounded-lg border border-destructive/20">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-destructive" />
              <span className="text-sm font-medium text-destructive">Potential Risks</span>
            </div>
            <ul className="space-y-2">
              {riskCatalysts.length > 0 ? riskCatalysts.map((risk, i) => (
                <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                  <span className="text-destructive">→</span>
                  {risk}
                </li>
              )) : <li className="text-sm text-muted-foreground">No specific risk catalysts returned in this review.</li>}
            </ul>
          </div>
        </div>

        {/* Key Insights */}
        <div className="pt-2 border-t border-border">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb className="w-4 h-4 text-warning" />
            <span className="text-sm font-medium text-foreground">Key Insights</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {keyInsights.length > 0 ? keyInsights.map((insight, i) => (
              <div key={i} className="flex items-start gap-2 p-2 bg-secondary/30 rounded text-sm">
                <span className="text-primary font-bold">{i + 1}.</span>
                <span className="text-muted-foreground">{insight}</span>
              </div>
            )) : <div className="p-2 bg-secondary/30 rounded text-sm text-muted-foreground">No key insights were generated from the available data.</div>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
